import React from 'react'

export type ChatBubbleVariant = 'ai' | 'user'

export interface ChatBubbleProps {
  variant: ChatBubbleVariant
  children: React.ReactNode
  testId?: string
  /**
   * When true, align bubbles for right-to-left scripts so the AI bubble stays
   * visually on the LEFT and the user bubble on the RIGHT (matching LTR
   * layouts). In an RTL flex row, `flex-start` resolves to the right edge and
   * `flex-end` to the left, so we invert the per-variant alignment.
   */
  rtl?: boolean
}

type Alignments = { readonly ltr: string; readonly rtl: string }

const alignClasses: Record<ChatBubbleVariant, Alignments> = {
  ai: { ltr: 'justify-start', rtl: 'justify-end' },
  user: { ltr: 'justify-end', rtl: 'justify-start' },
}

const variantClasses: Record<ChatBubbleVariant, string> = {
  ai: 'bg-neutral-100 text-neutral-900',
  user: 'bg-brand-500 text-neutral-0',
}

export function ChatBubble({
  variant,
  children,
  testId,
  rtl = false,
}: ChatBubbleProps): React.ReactElement {
  const justify = rtl ? alignClasses[variant].rtl : alignClasses[variant].ltr
  return (
    <div className={`flex ${justify}`} data-testid={testId}>
      <div
        className={[
          'max-w-chat-bubble rounded-xl px-4 py-2.5 text-sm',
          variantClasses[variant],
        ].join(' ')}
      >
        {children}
      </div>
    </div>
  )
}

export interface ChatTypingIndicatorProps {
  testId?: string
  /** Mirror the AI bubble alignment for right-to-left scripts. */
  rtl?: boolean
}

export function ChatTypingIndicator({
  testId,
  rtl = false,
}: ChatTypingIndicatorProps = {}): React.ReactElement {
  const justify = rtl ? 'justify-end' : 'justify-start'
  return (
    <div className={`flex ${justify}`}>
      <div
        className="rounded-xl bg-neutral-100 px-4 py-2.5 text-sm text-neutral-500"
        data-testid={testId}
      >
        <span className="inline-flex gap-1">
          <span className="chat-typing-dot chat-typing-dot--0">.</span>
          <span className="chat-typing-dot chat-typing-dot--1">.</span>
          <span className="chat-typing-dot chat-typing-dot--2">.</span>
        </span>
      </div>
    </div>
  )
}
